import React, { useMemo, useState } from 'react';
import { Drawer, Button, Spin, Progress, Tabs, Select } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
// Api
import {
  GET_CHANNEL_MONETIZATION_LEADERBOARD_CONTENT,
  LIST_CHANNEL_MONETIZATION_PROGRAMS,
} from 'api/channelMonetization/queries';
// Types
import {
  GetChannelMonetizationLeaderboardContent,
  GetChannelMonetizationLeaderboardContentVariables,
  GetChannelMonetizationLeaderboardContent_getChannelMonetizationLeaderboardContent as LeaderboardContent,
  GetChannelMonetizationLeaderboardContent_getChannelMonetizationLeaderboardContent_interviewStreams as ContentItem,
  GetChannelMonetizationLeaderboardContent_getChannelMonetizationLeaderboardContent_milestones as MilestoneProgress,
} from 'api/channelMonetization/types/GetChannelMonetizationLeaderboardContent';
import { ListChannelMonetizationPrograms } from 'api/channelMonetization/types/ListChannelMonetizationPrograms';
// Styles
import styles from './CreatorProgressDrawer.module.scss';

const { TabPane } = Tabs;

type CreatorProgressDrawerProps = {
  storeId: string | null;
  monthLabel: string | null;
  creatorName: string;
  open: boolean;
  onClose: () => void;
};

const MilestoneRow: React.FC<{ milestone: MilestoneProgress }> = ({
  milestone,
}) => (
  <div className={styles.milestone}>
    <div className={styles.milestoneHead}>
      <span className={styles.milestoneName}>{milestone.label}</span>
      <span
        className={
          milestone.completed
            ? `${styles.milestoneCount} ${styles.milestoneCountDone}`
            : styles.milestoneCount
        }
      >
        {milestone.current} / {milestone.target}
      </span>
    </div>
    <Progress
      percent={milestone.percent}
      size="small"
      status={milestone.completed ? 'success' : 'active'}
    />
  </div>
);

const ContentSection: React.FC<{ title: string; items: ContentItem[] }> = ({
  title,
  items,
}) => (
  <div className={styles.section}>
    <div className={styles.sectionTitle}>
      <span>{title}</span>
      <span className={styles.countBadge}>{items.length}</span>
    </div>
    {items.length === 0 ? (
      <div className={styles.empty}>No content in this period.</div>
    ) : (
      items.map((item) => (
        <div key={item.id} className={styles.item}>
          <div className={styles.itemLabel}>
            <span>{item.label}</span>
            <span className={styles.itemKind}>
              {item.kind}
              {item.date
                ? ` · ${new Date(item.date).toLocaleDateString()}`
                : ''}
            </span>
          </div>
          {item.url && (
            <Button
              type="link"
              icon={<LinkOutlined />}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View
            </Button>
          )}
        </div>
      ))
    )}
  </div>
);

const MonthContent: React.FC<{
  content: LeaderboardContent | undefined;
  loading: boolean;
}> = ({ content, loading }) => {
  if (loading && !content) {
    return <Spin />;
  }

  if (!content) {
    return <div className={styles.empty}>No content found.</div>;
  }

  return (
    <>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <span>Milestones ({content.monthLabel})</span>
        </div>
        {content.milestones.length === 0 ? (
          <div className={styles.empty}>
            No leaderboard progress recorded for this month.
          </div>
        ) : (
          content.milestones.map((milestone) => (
            <MilestoneRow key={milestone.type} milestone={milestone} />
          ))
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <span>Followers ({content.monthLabel})</span>
        </div>
        <div className={styles.followersBig}>{content.followersCount}</div>
      </div>

      <ContentSection
        title="Interview / Stream"
        items={content.interviewStreams}
      />
      <ContentSection title="Interview Now" items={content.interviewNow} />
      <ContentSection
        title="Brand & Athlete Mentions"
        items={content.mentions}
      />
      <ContentSection title="New Guest Invites" items={content.guestInvites} />
    </>
  );
};

const CreatorProgressDrawer: React.FC<CreatorProgressDrawerProps> = ({
  storeId,
  monthLabel,
  creatorName,
  open,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<string>('current');
  const [historyMonth, setHistoryMonth] = useState<string | null>(null);

  // Month shown by the active tab; history shows nothing until a month is picked
  const activeMonth = activeTab === 'current' ? monthLabel : historyMonth;

  const { data, loading } = useQuery<
    GetChannelMonetizationLeaderboardContent,
    GetChannelMonetizationLeaderboardContentVariables
  >(GET_CHANNEL_MONETIZATION_LEADERBOARD_CONTENT, {
    variables: {
      input: {
        storeId: storeId ?? '',
        monthLabel: activeMonth ?? '',
      },
    },
    skip: !open || !storeId || !activeMonth,
    fetchPolicy: 'cache-and-network',
  });

  // Program months are the only months the program ran — the valid history range
  const { data: programsData } = useQuery<ListChannelMonetizationPrograms>(
    LIST_CHANNEL_MONETIZATION_PROGRAMS,
    {
      skip: !open,
      fetchPolicy: 'cache-first',
    }
  );

  const historyMonthOptions = useMemo(
    () =>
      (programsData?.listChannelMonetizationPrograms ?? [])
        .map((program) => program.monthLabel)
        .filter((label) => label !== monthLabel)
        .sort()
        .reverse(),
    [programsData, monthLabel]
  );

  const content = data?.getChannelMonetizationLeaderboardContent;

  return (
    <Drawer
      title={`Progress — ${creatorName}`}
      placement="right"
      width={520}
      visible={open}
      onClose={onClose}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={`Current Month${monthLabel ? ` (${monthLabel})` : ''}`}
          key="current"
        >
          <MonthContent content={content} loading={loading} />
        </TabPane>

        <TabPane tab="History" key="history">
          <div className={styles.section}>
            <Select
              placeholder="Select month"
              style={{ width: 200 }}
              value={historyMonth ?? undefined}
              onChange={(value: string) => setHistoryMonth(value)}
              options={historyMonthOptions.map((label) => ({
                label,
                value: label,
              }))}
            />
          </div>

          {historyMonth ? (
            <MonthContent content={content} loading={loading} />
          ) : (
            <div className={styles.empty}>
              Pick a month to see this creator&apos;s history.
            </div>
          )}
        </TabPane>
      </Tabs>
    </Drawer>
  );
};

export default CreatorProgressDrawer;
